"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material"
import { TrendingUp, People, LocalHospital, AttachMoney, Print, Download } from "@mui/icons-material"

export default function ReportsAnalytics({ user }) {
  const [selectedPeriod, setSelectedPeriod] = useState("monthly")
  const [selectedReport, setSelectedReport] = useState("overview")

  // Sample data - in real app, this would come from API
  const overviewData = {
    totalPatients: 1234,
    totalAppointments: 456,
    totalRevenue: 2500000,
    totalStaff: 89,
    monthlyGrowth: 12.5,
    patientSatisfaction: 4.8,
  }

  const monthlyData = [
    { month: "January", patients: 120, revenue: 250000, appointments: 45 },
    { month: "February", patients: 135, revenue: 280000, appointments: 52 },
    { month: "March", patients: 142, revenue: 295000, appointments: 48 },
    { month: "April", patients: 158, revenue: 320000, appointments: 61 },
    { month: "May", patients: 167, revenue: 335000, appointments: 58 },
    { month: "June", patients: 174, revenue: 348000, appointments: 64 },
  ]

  const departmentData = [
    { department: "Cardiology", patients: 234, revenue: 580000 },
    { department: "Orthopedics", patients: 189, revenue: 472500 },
    { department: "Pediatrics", patients: 156, revenue: 312000 },
    { department: "Neurology", patients: 143, revenue: 357500 },
    { department: "Emergency", patients: 298, revenue: 596000 },
    { department: "Laboratory", patients: 445, revenue: 267000 },
  ]

  const topDoctors = [
    { name: "Dr. John Smith", department: "Cardiology", patients: 89, revenue: 223000 },
    { name: "Dr. Sarah Johnson", department: "Orthopedics", patients: 76, revenue: 190000 },
    { name: "Dr. Mike Wilson", department: "Neurology", patients: 68, revenue: 170000 },
    { name: "Dr. Emily Brown", department: "Pediatrics", patients: 82, revenue: 164000 },
    { name: "Dr. David Lee", department: "Emergency", patients: 94, revenue: 188000 },
  ]

  const renderOverviewReport = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <People sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Patients
                </Typography>
                <Typography variant="h4">{overviewData.totalPatients.toLocaleString()}</Typography>
                <Typography variant="body2" color="success.main">
                  +{overviewData.monthlyGrowth}% this month
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <LocalHospital sx={{ fontSize: 40, color: "info.main", mr: 2 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Appointments
                </Typography>
                <Typography variant="h4">{overviewData.totalAppointments.toLocaleString()}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <AttachMoney sx={{ fontSize: 40, color: "success.main", mr: 2 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4">₹{(overviewData.totalRevenue / 100000).toFixed(1)}L</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <TrendingUp sx={{ fontSize: 40, color: "warning.main", mr: 2 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Staff Members
                </Typography>
                <Typography variant="h4">{overviewData.totalStaff}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell>New Patients</TableCell>
                    <TableCell>Appointments</TableCell>
                    <TableCell>Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyData.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{row.patients}</TableCell>
                      <TableCell>{row.appointments}</TableCell>
                      <TableCell>₹{row.revenue.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )

  const renderDepartmentReport = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Department Performance
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell>Total Patients</TableCell>
                <TableCell>Revenue Generated</TableCell>
                <TableCell>Average per Patient</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departmentData.map((dept) => (
                <TableRow key={dept.department}>
                  <TableCell>{dept.department}</TableCell>
                  <TableCell>{dept.patients}</TableCell>
                  <TableCell>₹{dept.revenue.toLocaleString()}</TableCell>
                  <TableCell>₹{Math.round(dept.revenue / dept.patients).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )

  const renderDoctorReport = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Top Performing Doctors
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Doctor Name</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Patients Treated</TableCell>
                <TableCell>Revenue Generated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topDoctors.map((doctor) => (
                <TableRow key={doctor.name}>
                  <TableCell>{doctor.name}</TableCell>
                  <TableCell>{doctor.department}</TableCell>
                  <TableCell>{doctor.patients}</TableCell>
                  <TableCell>₹{doctor.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )

  const renderFinancialReport = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Revenue Breakdown
            </Typography>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Consultation Fees
              </Typography>
              <Typography variant="h6">₹8,50,000 (34%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Laboratory Tests
              </Typography>
              <Typography variant="h6">₹6,25,000 (25%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Pharmacy Sales
              </Typography>
              <Typography variant="h6">₹5,00,000 (20%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Procedures & Surgery
              </Typography>
              <Typography variant="h6">₹5,25,000 (21%)</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Expense Breakdown
            </Typography>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Staff Salaries
              </Typography>
              <Typography variant="h6">₹12,00,000 (48%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Medical Supplies
              </Typography>
              <Typography variant="h6">₹4,50,000 (18%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Equipment Maintenance
              </Typography>
              <Typography variant="h6">₹3,25,000 (13%)</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Utilities & Others
              </Typography>
              <Typography variant="h6">₹5,25,000 (21%)</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )

  const renderReport = () => {
    switch (selectedReport) {
      case "overview":
        return renderOverviewReport()
      case "department":
        return renderDepartmentReport()
      case "doctor":
        return renderDoctorReport()
      case "financial":
        return renderFinancialReport()
      default:
        return renderOverviewReport()
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Reports & Analytics</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Print />} sx={{ mr: 2 }}>
            Print Report
          </Button>
          <Button variant="contained" startIcon={<Download />}>
            Export Data
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Report Type</InputLabel>
            <Select value={selectedReport} label="Report Type" onChange={(e) => setSelectedReport(e.target.value)}>
              <MenuItem value="overview">Overview Report</MenuItem>
              <MenuItem value="department">Department Performance</MenuItem>
              <MenuItem value="doctor">Doctor Performance</MenuItem>
              <MenuItem value="financial">Financial Report</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Time Period</InputLabel>
            <Select value={selectedPeriod} label="Time Period" onChange={(e) => setSelectedPeriod(e.target.value)}>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {renderReport()}
    </Box>
  )
}
